import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { esnCompatibilitySchema } from '@app/shared';
import type { EsnCompatibilityDto, EsnCompatibilityInput } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { useNotify } from '../../components/NotifyProvider';
import { TextInput, applyServerErrors } from '../../components/form';
import { api } from '../../lib/api';

/**
 * Reporting > Check SIM Compatibility (PC270, switch `check-sim-compatibility`): the
 * reference's "Check ESN Compatibility" form. The carrier says whether the device works on
 * its network; nothing is stored.
 */
export function CheckCompatibilityPage() {
  const notify = useNotify();
  const check = useMutation({
    mutationFn: (input: EsnCompatibilityInput) =>
      api.post<EsnCompatibilityDto>('/esn-compatibility', input),
  });
  const { control, handleSubmit, setError } = useForm<EsnCompatibilityInput>({
    defaultValues: { esn: '' },
    resolver: zodResolver(esnCompatibilitySchema),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await check.mutateAsync(values);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const answer = check.data;

  return (
    <Box>
      <PageHeader title="Check ESN Compatibility" code="PC270" />
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 520 }}>
        <Typography sx={{ mb: 2 }}>Check the compatibility of your device</Typography>
        <Box
          component="form"
          noValidate
          onSubmit={(e) => void submit(e)}
          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
        >
          <TextInput
            control={control}
            name="esn"
            label="Enter your ESN below:"
            required
            fullWidth
            slotProps={{ htmlInput: { maxLength: 25 } }}
          />
          <Button type="submit" variant="contained" disabled={check.isPending} sx={{ mt: 1 }}>
            CHECK
          </Button>
        </Box>
        {answer ? (
          <Alert severity={answer.compatible ? 'success' : 'warning'} sx={{ mt: 2 }}>
            {answer.esn}: {answer.message}
          </Alert>
        ) : null}
      </Paper>
    </Box>
  );
}
